import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  FileText, ChevronRight, Calendar, Download, 
  ClipboardList, BarChart3, Wallet, CreditCard, TrendingUp, PieChart,
  Sparkles, FileSpreadsheet, Layers, AlertTriangle, Gauge, Pickaxe,
  Loader2, CheckCircle, X, Crown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============ RELATÓRIO MENSAL DE PRODUÇÃO (Monthly Reports by Year) ============

const MonthlyProductionReportPage = ({ language }) => {
  // Months data
  const months = [
    { id: 1, name: { pt: 'Janeiro', en: 'January' }, abbr: 'Jan' },
    { id: 2, name: { pt: 'Fevereiro', en: 'February' }, abbr: 'Feb' },
    { id: 3, name: { pt: 'Março', en: 'March' }, abbr: 'Mar' },
    { id: 4, name: { pt: 'Abril', en: 'April' }, abbr: 'Apr' },
    { id: 5, name: { pt: 'Maio', en: 'May' }, abbr: 'May' },
    { id: 6, name: { pt: 'Junho', en: 'June' }, abbr: 'Jun' },
    { id: 7, name: { pt: 'Julho', en: 'July' }, abbr: 'Jul' },
    { id: 8, name: { pt: 'Agosto', en: 'August' }, abbr: 'Aug' },
    { id: 9, name: { pt: 'Setembro', en: 'September' }, abbr: 'Sep' },
    { id: 10, name: { pt: 'Outubro', en: 'October' }, abbr: 'Oct' },
    { id: 11, name: { pt: 'Novembro', en: 'November' }, abbr: 'Nov' },
    { id: 12, name: { pt: 'Dezembro', en: 'December' }, abbr: 'Dec' },
  ];

  // Years data with report availability
  const years = [
    { 
      year: 2026, 
      availableMonths: [1, 2, 3], // Q1 2026 available
      status: 'current'
    },
    { 
      year: 2025, 
      availableMonths: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], // Full year
      status: 'complete'
    },
    { 
      year: 2024, 
      availableMonths: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], // Full year
      status: 'archived'
    },
  ];

  const getMonthStatus = (year, monthId) => {
    const yearData = years.find(y => y.year === year);
    if (!yearData) return 'unavailable';
    if (yearData.availableMonths.includes(monthId)) return 'available';
    return 'unavailable';
  };

  return (
    <div className="space-y-8" data-testid="monthly-production-report">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
          <span>{language === 'pt' ? 'Operações' : 'Operations'}</span>
          <ChevronRight className="w-4 h-4" />
          <span>{language === 'pt' ? 'Relatórios' : 'Reports'}</span>
          <ChevronRight className="w-4 h-4" />
          <span className="text-primary font-medium">{language === 'pt' ? 'Relatório Mensal de Produção' : 'Monthly Production Report'}</span>
        </div>
        <h1 className="font-serif text-3xl font-bold text-primary">
          {language === 'pt' ? 'Relatório Mensal de Produção' : 'Monthly Production Report'}
        </h1>
        <p className="text-slate-500 mt-2">
          {language === 'pt' 
            ? 'Arquivo de relatórios mensais de produção operacional'
            : 'Archive of monthly operational production reports'}
        </p>
      </div>

      {/* Years Grid - 3 columns */}
      <div className="grid grid-cols-3 gap-6">
        {years.map((yearData) => (
          <div key={yearData.year} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            {/* Year Header */}
            <div className="bg-gradient-to-br from-[#0A192F] to-[#1a2744] p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-gold-400" />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl font-bold text-white">
                      {yearData.year}
                    </h3>
                    <p className="text-slate-400 text-xs">
                      {yearData.status === 'current' && (language === 'pt' ? 'Ano Corrente' : 'Current Year')}
                      {yearData.status === 'complete' && (language === 'pt' ? 'Ano Completo' : 'Complete Year')}
                      {yearData.status === 'archived' && (language === 'pt' ? 'Arquivo' : 'Archived')}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-[10px] font-semibold ${
                  yearData.status === 'current' ? 'bg-emerald-500/20 text-emerald-400' :
                  yearData.status === 'complete' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-slate-500/20 text-slate-400'
                }`}>
                  {yearData.availableMonths.length}/{months.length}
                </span>
              </div>
            </div>

            {/* Monthly Reports List */}
            <div className="p-3 space-y-1.5 max-h-[480px] overflow-y-auto">
              {months.map((month) => {
                const status = getMonthStatus(yearData.year, month.id);
                const isAvailable = status === 'available';
                
                return (
                  <div 
                    key={month.id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                      isAvailable 
                        ? 'bg-slate-50 border-slate-100 hover:border-primary/30 hover:bg-slate-100 cursor-pointer group'
                        : 'bg-slate-50/50 border-slate-100/50 opacity-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold ${
                        isAvailable ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-400'
                      }`}>
                        {month.abbr}
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isAvailable ? 'text-slate-700 group-hover:text-primary' : 'text-slate-400'}`}>
                          {month.name[language]}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {isAvailable 
                            ? (language === 'pt' ? 'Disponível' : 'Available')
                            : (language === 'pt' ? 'Não disponível' : 'Not available')
                          }
                        </p>
                      </div>
                    </div>
                    {isAvailable && (
                      <button className="p-2 rounded hover:bg-white text-slate-400 hover:text-primary transition-colors">
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ RELATÓRIOS GERADOS PELA PLATAFORMA (Platform Reports) ============

const PlatformReportsPage = ({ language }) => {
  // Premium reports - all locked in pilot version
  const premiumReports = [
    {
      id: 'producao-receita',
      title: { pt: 'Produção e Receita', en: 'Production and Revenue' },
      description: { pt: 'Análise de produção de quilates e receitas geradas', en: 'Analysis of carat production and generated revenue' },
      icon: TrendingUp,
    },
    {
      id: 'mina-acesso',
      title: { pt: 'Mina e Acesso ao Minério', en: 'Mine and Ore Access' },
      description: { pt: 'Extração, remoção de estéril e acesso ao minério', en: 'Extraction, waste removal and ore access' },
      icon: Pickaxe,
    },
    {
      id: 'planta-recuperacao',
      title: { pt: 'Planta e Recuperação', en: 'Plant and Recovery' },
      description: { pt: 'Processamento, tratamento e taxas de recuperação', en: 'Processing, treatment and recovery rates' },
      icon: BarChart3,
    },
    {
      id: 'stocks-minerio',
      title: { pt: 'Stocks de Minério', en: 'Ore Stocks' },
      description: { pt: 'Níveis de stockpile, teores e disponibilidade', en: 'Stockpile levels, grades and availability' },
      icon: Layers,
    },
    {
      id: 'eficiencia-operacional',
      title: { pt: 'Eficiência Operacional', en: 'Operational Efficiency' },
      description: { pt: 'Utilização de equipamentos e métricas de eficiência', en: 'Equipment utilization and efficiency metrics' },
      icon: Gauge,
    },
    {
      id: 'risco-operacional',
      title: { pt: 'Risco Operacional', en: 'Operational Risk' },
      description: { pt: 'Análise de riscos e entrega do plano', en: 'Risk analysis and plan delivery' },
      icon: AlertTriangle,
    },
  ];

  const premiumNotice = {
    pt: 'Disponível apenas na versão final da solução, não incluído nesta versão piloto.',
    en: 'Available only in the final version of the solution, not included in this pilot version.'
  };

  return (
    <div className="space-y-8" data-testid="platform-reports">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
          <span>{language === 'pt' ? 'Operações' : 'Operations'}</span>
          <ChevronRight className="w-4 h-4" />
          <span>{language === 'pt' ? 'Relatórios' : 'Reports'}</span>
          <ChevronRight className="w-4 h-4" />
          <span className="text-primary font-medium">{language === 'pt' ? 'Relatórios Gerados pela Plataforma' : 'Platform Generated Reports'}</span>
        </div>
        <h1 className="font-serif text-3xl font-bold text-primary">
          {language === 'pt' ? 'Relatórios Gerados pela Plataforma' : 'Platform Generated Reports'}
        </h1>
        <p className="text-slate-500 mt-2">
          {language === 'pt' 
            ? 'Relatórios automatizados gerados com dados em tempo real'
            : 'Automated reports generated with real-time data'}
        </p>
      </div>

      {/* Reports Section */}
      <div className="bg-gradient-to-br from-[#0A192F] to-[#1a2744] rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-gold-400/20 flex items-center justify-center">
            <Crown className="w-5 h-5 text-gold-400" />
          </div>
          <div>
            <h2 className="font-serif text-xl font-bold text-white">
              {language === 'pt' ? 'Relatórios Premium' : 'Premium Reports'}
            </h2>
            <p className="text-slate-400 text-sm">
              {language === 'pt' ? 'Board-level com dados dinâmicos' : 'Board-level with dynamic data'}
            </p>
          </div>
        </div>

        {/* Reports Grid - All Premium/Locked */}
        <div className="grid grid-cols-2 gap-4">
          {premiumReports.map((report) => {
            const Icon = report.icon;
            
            return (
              <div 
                key={report.id}
                data-testid={`operations-report-${report.id}`}
                className="bg-white/95 rounded-xl p-5 flex items-center justify-between opacity-60"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-slate-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-500">
                      {report.title[language]}
                    </h3>
                    <p className="text-sm text-slate-400 mt-0.5">
                      {report.description[language]}
                    </p>
                    <p className="text-xs text-slate-400 mt-1 italic">
                      {premiumNotice[language]}
                    </p>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-slate-50">
                  <Crown className="w-5 h-5 text-amber-400" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Generate Custom Report Button - Also disabled */}
      <button 
        disabled
        className="w-full bg-gradient-to-r from-[#0A192F]/50 to-[#1a2744]/50 rounded-xl p-5 flex items-center justify-center gap-3 cursor-not-allowed opacity-60"
      >
        <Sparkles className="w-5 h-5 text-gold-400/50" />
        <span className="font-semibold text-white/50">
          {language === 'pt' ? 'Gerar Relatório Personalizado' : 'Generate Custom Report'}
        </span>
        <Crown className="w-4 h-4 text-amber-400/50 ml-2" />
      </button>
    </div>
  );
};

// ============ MAIN COMPONENT ============

const OperationsReports = ({ year, section }) => {
  const { language } = useLanguage();

  // Determine which page to show based on section
  if (section === 'relatorios-plataforma') {
    return <PlatformReportsPage language={language} />;
  }

  // Default: Relatório Mensal de Produção
  return <MonthlyProductionReportPage language={language} />;
};

export default OperationsReports;
